"use client";

import Image from "next/image";
import { useState } from "react";

import { FadeIn } from "@/components/global/utils/FadeIn";
import { PipeChip } from "@/components/global/sections/courseGallery/_shared";

interface MasterclassCard {
  _key?: string;
  image?: string;
  alt?: string;
}

interface MasterclassesSectionProps {
  eyebrow?: string;
  titleBefore?: string;
  titleEmphasis?: string;
  titleAfter?: string;
  description?: string;
  cards?: MasterclassCard[];
}

const fallbackCards: MasterclassCard[] = [
  { _key: "order-flow", image: "/courseDetail/masterclass-order-flow.png", alt: "The Complete Order Flow" },
  { _key: "institutional", image: "/courseDetail/masterclass-institutional.png", alt: "Institutional Edge" },
  { _key: "fundamentals", image: "/courseDetail/masterclass-fundamentals.png", alt: "Trading Fundamentals" },
  { _key: "dna", image: "/courseDetail/masterclass-dna.png", alt: "The Trader DNA Method" },
  { _key: "macro", image: "/courseDetail/masterclass-macro.png", alt: "Global Macro Investing" },
];

function Chevron({ direction }: { direction: "left" | "right" }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={`h-[3rem] w-[3rem] ${direction === "left" ? "rotate-180" : ""}`}>
      <path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function MasterclassesSection({
  eyebrow,
  titleBefore,
  titleEmphasis,
  titleAfter,
  description,
  cards,
}: MasterclassesSectionProps) {
  const displayedCards = cards?.length ? cards : fallbackCards;
  const trackCards =
    displayedCards.length > 1
      ? [...displayedCards, ...displayedCards]
      : displayedCards;
  const [active, setActive] = useState(0);
  const last = Math.max(0, displayedCards.length - 1);

  return (
    <section className="flex h-[120.75rem] w-screen flex-col items-center gap-[4.875rem] overflow-hidden bg-black pt-[10rem] pb-[8rem] lg:h-[127rem] lg:pt-[2.375rem] lg:pb-[20rem]">
      <FadeIn className="flex w-full flex-col items-center text-center lg:w-[64.75rem]">
        <div className="flex flex-col items-center gap-[3rem]">
          <PipeChip className="text-[1.75rem] leading-[2.25rem]">
            {eyebrow ?? "Masterclasses"}
          </PipeChip>
          <div className="flex flex-col items-center gap-[4rem] text-white">
            <h2 className="m-0 w-[36.25rem] font-news text-[4.5rem] leading-[1.1] font-normal tracking-[-0.09rem] lg:w-[67.625rem] lg:text-[7.5rem] lg:tracking-[-0.15rem]">
              {titleBefore ?? "Everything about"} {" "}
              <em className="font-normal">{titleEmphasis ?? "trading"}</em>{" "}
              {titleAfter ?? "in one place."}
            </h2>
            <p className="m-0 w-[41.75rem] font-geist text-[2.25rem] leading-[1.5] font-normal text-white/60 lg:w-full">
              {description ??
                "See what you can expect inside. Learn directly from World Champions, Market Makers, and Institutional Traders."}
            </p>
          </div>
        </div>
      </FadeIn>

      <div className="flex w-full flex-col items-center gap-[4rem]">
        <div className="w-full overflow-hidden pl-[2rem] lg:w-[210rem] lg:pl-0">
          <div
            className="flex w-max gap-[2rem] transition-transform duration-500 ease-out lg:transform-none"
            style={{ transform: `translateX(-${active * 42.375}rem)` }}>
            {trackCards.map((card, index) => {
              const isClone = index >= displayedCards.length;

              return (
                <article
                  key={`${card._key ?? card.image ?? "masterclass"}-${index}`}
                  aria-hidden={isClone || undefined}
                  className="relative h-[56.5rem] w-[40.375rem] shrink-0 overflow-hidden rounded-[1.5rem]">
                  {card.image && (
                    <Image
                      src={card.image}
                      alt={isClone ? "" : (card.alt ?? "Masterclass")}
                      fill
                      sizes="20rem"
                      className="rounded-[1.5rem] object-cover"
                    />
                  )}
                </article>
              );
            })}
          </div>
        </div>

        <div className="flex h-[6rem] w-[31.25rem] items-center justify-between">
          <button
            type="button"
            aria-label="Previous masterclass"
            onClick={() => setActive((value) => (value <= 0 ? last : value - 1))}
            className="flex h-[6rem] w-[6rem] items-center justify-center rounded-full border border-white/10 bg-white/[0.09] text-white/60 backdrop-blur-[2.5rem]">
            <Chevron direction="left" />
          </button>
          <div className="flex h-[1rem] items-center gap-[1.5rem]">
            {displayedCards.map((card, index) => (
              <button
                key={card._key ?? index}
                type="button"
                aria-label={`Go to masterclass ${index + 1}`}
                onClick={() => setActive(index)}
                className={`h-[1rem] rounded-[0.25rem] bg-white transition-all ${active === index ? "w-[4rem]" : "w-[1rem] opacity-40"}`}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label="Next masterclass"
            onClick={() => setActive((value) => (value >= last ? 0 : value + 1))}
            className="flex h-[6rem] w-[6rem] items-center justify-center rounded-full bg-white text-black">
            <Chevron direction="right" />
          </button>
        </div>
      </div>
    </section>
  );
}
